import { scoreEntry, queryContext } from './entry-scorer'
import { parseQuery } from './query-parser'
import { bigramMask } from './search-entry'

// SearchIndex.cs's pure parts, line for line: EvaluateQuery (score every
// entry; filter-independent) and BuildResultsAndCounts (type chip and
// overlays, boosts, the sort, the limit, the chip counts).

const f = Math.fround

export const KIND_COURSE = 'course'
export const KIND_FOLDER = 'folder'

export const TYPE_ALL = 'all'
export const TYPE_COURSES = 'courses'
export const TYPE_FOLDERS = 'folders'

export const RESULT_LIMIT = 50

const quotedQueryMask = tokens => {
  let lo = 0
  let hi = 0
  for (const token of tokens) {
    if (!token.quoted) continue
    const m = bigramMask(token.text)
    lo |= m.lo
    hi |= m.hi
  }
  return { lo, hi }
}

// { query, matches: [{ index, baseScore, nameIndices, pathIndices }] }:
// index is the entry's position in `entries`.
export const evaluateQuery = (entries, query, now) => {
  const matches = []
  if (!entries || entries.length === 0 || !query) return { query, matches }

  const tokens = parseQuery(query)
  if (tokens.length === 0) return { query, matches }

  // Bigram gate, quoted tokens only (a pure prefilter).
  const gate = quotedQueryMask(tokens)
  const gated = gate.lo !== 0 || gate.hi !== 0
  const context = queryContext(tokens)

  for (let i = 0; i < entries.length; i++) {
    const entry = entries[i]
    if (gated && ((entry.bigrams.lo & gate.lo) !== gate.lo || (entry.bigrams.hi & gate.hi) !== gate.hi)) continue

    const r = scoreEntry(entry, tokens, now, context)
    if (r.score < 0) continue
    matches.push({ index: i, baseScore: r.score, nameIndices: r.nameIndices, pathIndices: r.pathIndices })
  }
  return { query, matches }
}

const passesTypeFilter = (kind, type) => {
  switch (type) {
    case TYPE_FOLDERS:
      return kind === KIND_FOLDER
    case TYPE_COURSES:
      return kind === KIND_COURSE
    default:
      return true
  }
}

const kindBoost = kind => (kind === KIND_COURSE ? 2 : kind === KIND_FOLDER ? 1 : 0)

// Score, then the newer entry, then the lower-cased name (by UTF-16 code
// unit, C#'s CompareOrdinal), then the id: a total order.
export const compareResults = (a, b) => {
  if (b.score > a.score) return 1
  if (b.score < a.score) return -1
  const au = a.entry.updatedAt === null ? -Infinity : a.entry.updatedAt
  const bu = b.entry.updatedAt === null ? -Infinity : b.entry.updatedAt
  if (bu > au) return 1
  if (bu < au) return -1
  if (a.entry.lowerName < b.entry.lowerName) return -1
  if (a.entry.lowerName > b.entry.lowerName) return 1
  return a.entry.id < b.entry.id ? -1 : a.entry.id > b.entry.id ? 1 : 0
}

// filters: { type: 'all' | 'courses' | 'folders', recentOnly, favoritesOnly,
// installedOnly }. Predicates may be null (as in C#). Each chip's count is
// the rows that chip would show with the other filters kept, so the active
// chip's count is the number of rows (before the limit).
//
// { results: [{ entry, score, nameIndices, pathIndices }], counts }
export const buildResultsAndCounts = (set, entries, filters = {}, limit = RESULT_LIMIT, isRecent = null, isFavorite = null, isInstalled = null) => {
  const counts = { all: 0, courses: 0, folders: 0, recent: 0, favorites: 0, installed: 0 }
  const results = []
  if (!set) return { results, counts }

  const type = filters.type || TYPE_ALL
  const recentOnly = !!filters.recentOnly
  const favoritesOnly = !!filters.favoritesOnly
  const installedOnly = !!filters.installedOnly

  for (const match of set.matches) {
    const entry = entries[match.index]

    const r = !!isRecent && !!isRecent(entry)
    const fav = !!isFavorite && !!isFavorite(entry)
    const ins = !!isInstalled && !!isInstalled(entry)

    const passesOverlays = (!recentOnly || r) && (!favoritesOnly || fav) && (!installedOnly || ins)
    if (passesOverlays) {
      counts.all++
      if (entry.kind === KIND_FOLDER) counts.folders++
      else if (entry.kind === KIND_COURSE) counts.courses++
    }
    const passesCurrentType = passesTypeFilter(entry.kind, type)
    if (passesCurrentType) {
      if (r && (!favoritesOnly || fav) && (!installedOnly || ins)) counts.recent++
      if (fav && (!recentOnly || r) && (!installedOnly || ins)) counts.favorites++
      if (ins && (!recentOnly || r) && (!favoritesOnly || fav)) counts.installed++
    }

    if (!passesCurrentType) continue
    if (recentOnly && isRecent && !r) continue
    if (favoritesOnly && isFavorite && !fav) continue
    if (installedOnly && isInstalled && !ins) continue

    let score = f(match.baseScore + kindBoost(entry.kind))
    if (r) score = f(score + 10)
    if (fav) score = f(score + 6)
    if (ins) score = f(score + 4)

    results.push({ entry, score, nameIndices: match.nameIndices, pathIndices: match.pathIndices })
  }

  results.sort(compareResults)
  if (limit >= 0 && results.length > limit) results.length = limit
  return { results, counts }
}
